import adminStyle from "../../../styles/dashboard-admin.module.scss";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { getServerURL } from "../../../utils/funcs/get-serverURL";

export default function Admin({ user }: any) {
  const { getToken } = useAuth();
  let navigate = useNavigate();
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [loadingApproval, setLoadingApproval] = useState(true);
  const [savingApproval, setSavingApproval] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState("");

  const serverURL = getServerURL();

  const fetchEICRSettings = useCallback(async () => {
    try {
      setLoadingApproval(true);
      const response = await axios.get(`${serverURL}/eicr-settings`);
      if (response.data.success) {
        setApprovalRequired(Boolean(response.data.eicrApprovalRequired));
      }
    } catch (error) {
      console.error("Failed to load EICR settings", error);
      setApprovalMessage("Could not load EICR approval setting.");
    } finally {
      setLoadingApproval(false);
    }
  }, [serverURL]);

  useEffect(() => {
    fetchEICRSettings();
  }, [fetchEICRSettings]);

  const updateApprovalMode = async (enabled: boolean) => {
    try {
      const token = await getToken();
      if (!token) {
        setApprovalMessage("Unable to verify admin session.");
        return;
      }

      setSavingApproval(true);
      setApprovalMessage("");

      const response = await axios.patch(`${serverURL}/eicr-settings`, {
        eicrApprovalRequired: enabled,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setApprovalRequired(Boolean(response.data.eicrApprovalRequired));
        setApprovalMessage(
          enabled
            ? "Approval mode enabled. New EICRs will come to admin first."
            : "Approval mode disabled. New EICRs will send directly to customer."
        );
      }
    } catch (error) {
      console.error("Failed to update EICR settings", error);
      setApprovalMessage("Failed to update EICR approval setting.");
    } finally {
      setSavingApproval(false);
    }
  };

  return (
    <div>
      <div className={adminStyle.settingsCard}>
        <h3>
          EICR Customer Email Approval
          <span className={`${adminStyle.statusBadge} ${
            loadingApproval 
              ? adminStyle.loading 
              : approvalRequired 
                ? adminStyle.active 
                : adminStyle.inactive
          }`}>
            <span className={`${adminStyle.statusIndicator} ${
              approvalRequired ? adminStyle.active : adminStyle.inactive
            }`}></span>
            {loadingApproval ? "Loading..." : approvalRequired ? "ENABLED" : "DISABLED"}
          </span>
        </h3>
        <p className={adminStyle.settingsDescription}>
          {loadingApproval
            ? "Loading current approval setting..."
            : approvalRequired
              ? "⚡ Approval mode is active. New EICR submissions will be sent to admin for review before being forwarded to customers."
              : "✓ Direct mode is active. New EICR submissions are automatically sent to customers without admin approval."}
        </p>
        <div className={adminStyle.settingsActions}>
          <button
            type="button"
            className={approvalRequired ? adminStyle.dangerButton : adminStyle.activeButton}
            disabled={savingApproval || loadingApproval}
            onClick={() => updateApprovalMode(!approvalRequired)}
          >
            {savingApproval 
              ? 'Updating...' 
              : approvalRequired 
                ? 'Disable Approval Mode' 
                : 'Enable Approval Mode'}
          </button>
        </div>
        {approvalMessage && (
          <p className={`${adminStyle.settingsMessage} ${
            approvalMessage.includes('Failed') || approvalMessage.includes('Could not') || approvalMessage.includes('Unable')
              ? adminStyle.error 
              : adminStyle.success
          }`}>
            {approvalMessage}
          </p>
        )}
      </div>
    </div>
  );
}
